import React, { useLayoutEffect, useRef, useState } from 'react';
import { ArrowLeft } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { resolveLineRevealProgress } from '../components/ui/LetterRevealSection';
import { ContactSection } from '../components/ui/ContactSection';
import beeIllustration from '../assets/heco-maxim-bee.png';
import baristaIllustration from '../assets/barista-illustration.png';
import penIllustration from '../assets/about-pen-illustration.png';
import zenGardenIllustration from '../assets/heco-zen-garden-illustration.png';
import americanoInterest from '../assets/interest-americano.png';
import matchaInterest from '../assets/interest-matcha.png';
import pilatesInterest from '../assets/interest-pilates.png';
import puzzleInterest from '../assets/interest-puzzle.png';
import racketInterest from '../assets/interest-racket.png';
import salmonInterest from '../assets/interest-salmon.png';
import milkPitcherInterest from '../assets/interest-milk-pitcher.png';
import watercolorInterest from '../assets/interest-watercolor.png';
import './AboutPage.css';

type AboutStoryBlock = {
  text: string;
  italic?: boolean;
  spacing?: 'normal' | 'compact';
};

const ABOUT_STORY_BLOCKS: AboutStoryBlock[] = [
  {
    text: 'I’m curious about trends, everyday products, and how data and AI can explain what’s changing.',
  },
  {
    text: 'I also work part-time as a barista and write little notes about learning, work, and everyday life.',
  },
  {
    text: 'On Madison Avenue, there’s a saying: “Your life is your Big Idea.”',
  },
  {
    text: 'It was the line that first drew me into advertising. Years later, it gave me the courage to start over.',
  },
  { text: 'In code, I found new ways of thinking.' },
  {
    text: 'In interfaces, new languages for storytelling.',
    spacing: 'compact',
  },
  { text: 'Technology did not replace creativity.' },
  { text: 'It simply gave it a new voice.', spacing: 'compact' },
  { text: 'I never abandoned my beginning.' },
  { text: 'I just picked up a different pen', spacing: 'compact' },
  { text: 'and kept writing.', spacing: 'compact' },
];

type InterestItem = {
  label: string;
  note: string;
  image: string;
  tilt: number;
  scale?: number;
};

const INTEREST_ITEMS: InterestItem[] = [
  {
    label: 'Americano',
    note: 'slow mornings',
    image: americanoInterest,
    tilt: -3,
    scale: 1.42,
  },
  { label: 'Matcha', note: 'quiet energy', image: matchaInterest, tilt: 3, scale: 1.08 },
  { label: 'Pilates', note: 'strength and balance', image: pilatesInterest, tilt: -2 },
  { label: 'Puzzles', note: 'patient focus', image: puzzleInterest, tilt: 3 },
  { label: 'Tennis', note: 'rhythm and movement', image: racketInterest, tilt: -4 },
  { label: 'Salmon', note: 'a favourite comfort', image: salmonInterest, tilt: 2 },
  {
    label: 'Latte Art',
    note: 'practice and precision',
    image: milkPitcherInterest,
    tilt: -3,
    scale: 1.18,
  },
  { label: 'Watercolor', note: 'colour without rules', image: watercolorInterest, tilt: 3 },
];

const tokenize = (text: string) => text.match(/\S+|\s+/g) ?? [];

export const getAboutLineRevealProgress = (
  scrollY: number,
  startScroll: number,
  duration: number
) =>
  resolveLineRevealProgress(
    (scrollY - startScroll) / Math.max(1, duration),
    0,
    1
  );

type StoryBlockProps = {
  block: AboutStoryBlock;
  blockIndex: number;
  lead?: boolean;
};

const StoryBlock: React.FC<StoryBlockProps> = ({ block, blockIndex, lead }) => (
  <p
    className={`about-story-page__block${lead ? ' is-lead' : ''}${
      block.italic ? ' is-italic' : ''
    }${block.spacing === 'compact' ? ' is-compact' : ''}`}
    aria-label={block.text}
  >
    {tokenize(block.text).map((token, tokenIndex) => {
      if (/^\s+$/.test(token)) {
        return (
          <React.Fragment key={`${blockIndex}-${tokenIndex}`}>
            {token}
          </React.Fragment>
        );
      }

      return (
        <span
          key={`${blockIndex}-${tokenIndex}`}
          className="about-story-page__word"
          aria-hidden="true"
        >
          {token}
        </span>
      );
    })}
  </p>
);

export const AboutPage: React.FC = () => {
  const storyRef = useRef<HTMLElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const [activeInterest, setActiveInterest] = useState<string | null>(null);

  useLayoutEffect(() => {
    document.body.dataset.aboutTheme = 'active';

    return () => {
      delete document.body.dataset.aboutTheme;
    };
  }, []);

  useLayoutEffect(() => {
    const story = storyRef.current;
    const copy = copyRef.current;

    if (!story || !copy) {
      return undefined;
    }

    const words = Array.from(
      copy.querySelectorAll<HTMLElement>('.about-story-page__word')
    );

    if (words.length === 0) {
      return undefined;
    }

    let visualLines: Array<{
      words: HTMLElement[];
      documentTop: number;
    }> = [];
    let resizeFrame = 0;

    const measureVisualLines = () => {
      const nextLines: HTMLElement[][] = [];
      let currentTop: number | null = null;

      words.forEach((word) => {
        const wordTop = word.getBoundingClientRect().top;

        if (currentTop === null || Math.abs(wordTop - currentTop) > 2) {
          nextLines.push([]);
          currentTop = wordTop;
        }

        nextLines[nextLines.length - 1].push(word);
      });

      visualLines = nextLines.map((line) => ({
        words: line,
        documentTop:
          (line[0]?.getBoundingClientRect().top ?? 0) + window.scrollY,
      }));
      copy.style.setProperty('--about-story-line-count', String(visualLines.length));
    };

    const updateReveal = () => {
      const viewportHeight = Math.max(1, window.innerHeight);
      const scrollY = window.scrollY;
      let initialLineIndex = 0;

      visualLines.forEach((line) => {
        const naturalStart = line.documentTop - viewportHeight * 0.68;
        const isInitiallyVisible = naturalStart <= 0;
        const startScroll = isInitiallyVisible
          ? initialLineIndex * viewportHeight * 0.018
          : naturalStart;
        const duration = viewportHeight * (isInitiallyVisible ? 0.14 : 0.36);
        const lineProgress = getAboutLineRevealProgress(
          scrollY,
          startScroll,
          duration
        ).toFixed(4);

        if (isInitiallyVisible) {
          initialLineIndex += 1;
        }

        line.words.forEach((word) => {
          word.style.setProperty('--about-line-progress', lineProgress);
        });
      });
    };

    const measureAndUpdate = () => {
      measureVisualLines();
      updateReveal();
    };

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      measureVisualLines();
      visualLines.forEach((line) => {
        line.words.forEach((word) => {
          word.style.setProperty('--about-line-progress', '1');
        });
      });
      return undefined;
    }

    measureAndUpdate();

    const trigger = ScrollTrigger.create({
      trigger: story,
      start: 'top top',
      end: 'bottom 20%',
      scrub: 0.75,
      invalidateOnRefresh: true,
      onUpdate: updateReveal,
      onRefresh: measureAndUpdate,
    });

    updateReveal();

    const handleResize = () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeFrame = window.requestAnimationFrame(() => {
        measureAndUpdate();
      });
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(copy);

    return () => {
      window.cancelAnimationFrame(resizeFrame);
      resizeObserver.disconnect();
      trigger?.kill();
    };
  }, []);

  return (
    <main className="about-story-page">
      <header className="about-story-page__header">
        <Link className="about-story-page__brand" to="/" aria-label="Mia Miao home">
          Mia Miao
        </Link>
        <Link className="about-story-page__back" to="/" aria-label="Back to home">
          <ArrowLeft size={24} weight="regular" aria-hidden="true" />
        </Link>
      </header>

      <section
        ref={storyRef}
        className="about-story-page__story"
        aria-label="About Mia"
      >
        <div ref={copyRef} className="about-story-page__content">
          <div className="about-story-page__opening">
            <div className="about-story-page__opening-copy">
              <div
                className="about-story-page__curiosity-copy"
                data-opening-pair="curiosity"
              >
                <StoryBlock block={ABOUT_STORY_BLOCKS[0]} blockIndex={0} lead />
              </div>

              <figure
                className="about-story-page__barista-illustration-wrap"
                data-opening-pair="barista"
              >
                <img
                  className="about-story-page__barista-illustration"
                  src={baristaIllustration}
                  alt="Hand-drawn barista preparing pour-over coffee"
                />
              </figure>
            </div>

            <div className="about-story-page__opening-aside">
              <figure
                className="about-story-page__illustration-wrap"
                data-opening-pair="curiosity"
              >
                <img
                  className="about-story-page__illustration"
                  src={beeIllustration}
                  alt="Hand-drawn bee character holding a coffee cup"
                />
              </figure>

              <div
                className="about-story-page__illustration-note"
                data-opening-pair="barista"
              >
                <StoryBlock
                  block={ABOUT_STORY_BLOCKS[1]}
                  blockIndex={1}
                  lead
                />
              </div>
            </div>
          </div>

          <div className="about-story-page__continuation">
            <div className="about-story-page__continuation-column is-left">
              {ABOUT_STORY_BLOCKS.slice(2, 4).map((block, blockIndex) => (
                <StoryBlock
                  key={block.text}
                  block={block}
                  blockIndex={blockIndex + 2}
                />
              ))}
            </div>

            <figure
              className="about-story-page__continuation-media-slot is-right"
              data-about-image-slot="right"
              aria-hidden="true"
            >
              <img
                className="about-story-page__zen-garden-illustration"
                src={zenGardenIllustration}
                alt=""
              />
            </figure>

            <figure
              className="about-story-page__continuation-media-slot is-left"
              data-about-image-slot="left"
            >
              <img
                className="about-story-page__pen-illustration"
                src={penIllustration}
                alt="Hand-drawn hand writing with a flower-shaped pen"
              />
            </figure>

            <div className="about-story-page__continuation-column is-right">
              {ABOUT_STORY_BLOCKS.slice(4).map((block, blockIndex) => (
                <StoryBlock
                  key={block.text}
                  block={block}
                  blockIndex={blockIndex + 4}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="about-story-page__interests"
        aria-labelledby="about-interests-title"
      >
        <div className="about-story-page__interests-inner">
          <div className="about-story-page__interests-intro">
            <span className="about-story-page__interests-kicker">Interests</span>
            <h2
              id="about-interests-title"
              className="about-story-page__interests-title"
            >
              The rituals, tools, and ideas I keep returning to—small things that
              make everyday life feel more alive.
            </h2>
          </div>

          <ul className="about-story-page__interests-grid">
            {INTEREST_ITEMS.map(({ label, note, image, tilt, scale = 1 }) => {
              const isActive = activeInterest === label;

              return (
                <li
                  key={label}
                  className="about-story-page__interest-item"
                  style={
                    {
                      '--interest-tilt': `${tilt}deg`,
                      '--interest-image-scale': scale,
                    } as React.CSSProperties
                  }
                >
                  <button
                    type="button"
                    className={`about-story-page__interest${
                      isActive ? ' is-active' : ''
                    }`}
                    aria-label={`${label}: ${note}`}
                    aria-pressed={isActive}
                    onClick={() =>
                      setActiveInterest((current) =>
                        current === label ? null : label
                      )
                    }
                  >
                    <span className="about-story-page__interest-icon" aria-hidden="true">
                      <img src={image} alt="" />
                    </span>
                    <span className="about-story-page__interest-label">
                      <span>{label}</span>
                      <small>{note}</small>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </section>

      <div className="about-story-page__contact" data-about-section="contact">
        <ContactSection />
      </div>
    </main>
  );
};
